import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
  ChipsetIcon,
  DownArrowIcon,
} from "../../Common/SvgIcons/WorkflowIcons";

const capitalize = (value) =>
  String(value || "")
    .charAt(0)
    .toUpperCase() + String(value || "").slice(1);

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const StatusDot = ({ status }) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  const isActive = normalizedStatus === "active" || normalizedStatus === "online";
  const isInActive = ["inactive", "offline", "off"].includes(normalizedStatus);

  return (
    <span
      className="eval-target-hardware-menu__status-dot"
      style={{
        backgroundColor: isActive ? "#28a745" : isInActive ? "#dc3545" : "#f5ff00",
      }}
      aria-hidden="true"
    />
  );
};

export default function EvaluationTargetHardwareMenu({
  devices = {},
  selectedTargetHardware,
  onTargetHardwareChange,
  selectedFamily = "",
  onFamilyChange,
  selectedChipset = "",
  onChipsetChange,
  selectedDevice = "",
  onDeviceChange,
  getDeviceStatus = () => "Reserved",
  disabled = false,
  familyRef,
  chipsetRef,
  panelPlacement = "below",
  panelAlign = "left",
  autoAlignAbove = "right",
  autoAlignBelow = "center",
  useDropdownSelections = false,
  centerHardwareFamily = false,
  triggerId,
}) {
  const [open, setOpen] = useState(false);
  const [resolvedPlacement, setResolvedPlacement] = useState(panelPlacement);
  const [resolvedAlign, setResolvedAlign] = useState(panelAlign);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const supportsTargetSelection = typeof onTargetHardwareChange === "function";
  const isHostTarget = supportsTargetSelection && selectedTargetHardware === "Host";

  const resolvePanelPosition = useCallback(() => {
    if (panelPlacement !== "auto") {
      setResolvedPlacement(panelPlacement);
      setResolvedAlign(panelAlign);
      return;
    }

    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight || 320;
    const viewportPadding = 16;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;

    if (spaceBelow >= panelHeight) {
      setResolvedPlacement("below");
      setResolvedAlign(autoAlignBelow);
      return;
    }

    if (spaceAbove >= panelHeight) {
      setResolvedPlacement("above");
      setResolvedAlign(autoAlignAbove);
      return;
    }

    if (spaceAbove >= spaceBelow) {
      setResolvedPlacement("above");
      setResolvedAlign(autoAlignAbove);
    } else {
      setResolvedPlacement("below");
      setResolvedAlign(autoAlignBelow);
    }
  }, [autoAlignAbove, autoAlignBelow, panelAlign, panelPlacement]);

  const families = useMemo(
    () => Object.keys(devices || {}),
    [devices]
  );

  const chipsets = useMemo(() => {
    if (!selectedFamily || !devices?.[selectedFamily]) return [];
    return Object.keys(devices[selectedFamily]);
  }, [devices, selectedFamily]);

  const chipsetOptions = useMemo(
    () =>
      chipsets.map((chipset) => ({
        value: chipset,
        label: String(chipset).toUpperCase(),
      })),
    [chipsets]
  );

  const deviceList = useMemo(() => {
    if (!selectedFamily || !selectedChipset) return [];
    return devices?.[selectedFamily]?.[selectedChipset] || [];
  }, [devices, selectedChipset, selectedFamily]);

  const deviceOptions = useMemo(
    () => [
      { value: "", label: "Any available device", status: "" },
      ...deviceList.map((device) => ({
        value: device,
        label: device,
        status: getDeviceStatus(device),
      })),
    ],
    [deviceList, getDeviceStatus]
  );

  const hardwareSummary = useMemo(() => {
    if (isHostTarget) return [];

    const parts = [];
    if (selectedFamily) parts.push(capitalize(selectedFamily));
    if (selectedChipset) parts.push(String(selectedChipset).toUpperCase());
    if (selectedDevice) parts.push(selectedDevice);
    return parts;
  }, [isHostTarget, selectedDevice, selectedChipset, selectedFamily]);

  const triggerParts = hardwareSummary;
  const selectedDeviceStatus = selectedDevice ? getDeviceStatus(selectedDevice) : "";
  const triggerLabel = triggerParts.length
    ? triggerParts.join(" · ")
    : "Target Hardware";
  const compactTriggerLabel = triggerParts.length
    ? triggerParts.slice(0, 2).join(" · ")
    : "Target Hardware";
  const hiddenTriggerPartCount = Math.max(0, triggerParts.length - 2);

  useEffect(() => {
    if (panelPlacement !== "auto") {
      setResolvedPlacement(panelPlacement);
      setResolvedAlign(panelAlign);
    }
  }, [panelAlign, panelPlacement]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const isPortaledSelectMenu = event.target?.closest?.(
        ".eval-target-device-select__menu"
      );
      if (!rootRef.current?.contains(event.target) && !isPortaledSelectMenu) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || panelPlacement !== "auto") return;
    resolvePanelPosition();
  }, [
    deviceList.length,
    isHostTarget,
    open,
    panelPlacement,
    resolvePanelPosition,
    selectedChipset,
    selectedFamily,
  ]);

  useEffect(() => {
    if (!open || panelPlacement !== "auto") return undefined;

    const handleViewportChange = () => resolvePanelPosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, panelPlacement, resolvePanelPosition]);

  const toggle = () => {
    if (disabled) return;
    setOpen((current) => !current);
  };

  const renderDeviceSelect = (id = "eval-target-device-select") => {
    if (useDropdownSelections) {
      return (
        <Select
          inputId={id}
          className="eval-target-hardware-panel__react-select"
          classNamePrefix="eval-target-device-select"
          options={deviceOptions}
          value={deviceOptions.find((option) => option.value === (selectedDevice || ""))}
          onChange={(option) => {
            onDeviceChange?.(option?.value || "");
            setOpen(false);
          }}
          isDisabled={!selectedChipset || deviceList.length === 0}
          isSearchable={false}
          menuPlacement={resolvedPlacement === "above" ? "top" : "auto"}
          menuPosition="fixed"
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuShouldScrollIntoView={false}
          styles={selectPortalStyles}
          formatOptionLabel={(option) => (
            <span className="eval-target-hardware-panel__device-option" title={option.status || undefined}>
              {option.status && <StatusDot status={option.status} />}
              <span>{option.label}</span>
            </span>
          )}
        />
      );
    }

    return (
      <select
        id={id}
        className="eval-target-hardware-panel__select"
        value={selectedDevice || ""}
        disabled={!selectedChipset || deviceList.length === 0}
        onChange={(event) => {
          onDeviceChange?.(event.target.value);
          setOpen(false);
        }}
      >
        <option value="">Any available device</option>
        {deviceList.map((device) => (
          <option key={device} value={device}>
            {device}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div
      ref={rootRef}
      className={`eval-target-hardware-inline${open ? " is-open" : ""}`}
    >
      <button
        id={triggerId}
        ref={triggerRef}
        type="button"
        className="eval-target-hardware-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-label={`Configure evaluation target hardware: ${triggerLabel}`}
        title={triggerLabel}
        onClick={toggle}
      >
        <ChipsetIcon className="eval-target-hardware-trigger__icon wf-icon-small" />
        {selectedDeviceStatus && <StatusDot status={selectedDeviceStatus} />}
        <span className="eval-target-hardware-trigger__value">{compactTriggerLabel}</span>
        {hiddenTriggerPartCount > 0 && (
          <span
            className="eval-target-hardware-trigger__more"
            aria-label={`${hiddenTriggerPartCount} additional selected option${hiddenTriggerPartCount === 1 ? "" : "s"}`}
          >
            +{hiddenTriggerPartCount}
          </span>
        )}
        <DownArrowIcon className="eval-target-hardware-trigger__chevron" />
      </button>

      {open && (
        <div
          ref={panelRef}
          className={`eval-target-hardware-panel eval-target-hardware-panel--${resolvedPlacement} eval-target-hardware-panel--align-${resolvedAlign}`}
          style={{left:'none'}}
        >
           {/* {supportsTargetSelection && (
            <div className="eval-target-hardware-panel__section">
              <label className="eval-target-hardware-panel__label">Evaluation target</label>
              <div className="eval-target-hardware-panel__pills">
                <button
                  type="button"
                  className={`eval-target-hardware-panel__pill${
                    isHostTarget ? " is-active" : ""
                  }`}
                  onClick={() => {
                    onTargetHardwareChange("Host");
                    onDeviceChange?.("");
                  }}
                >
                  Host
                </button>
                <button
                  type="button"
                  className={`eval-target-hardware-panel__pill${
                    !isHostTarget ? " is-active" : ""
                  }`}
                  onClick={() => onTargetHardwareChange("edge_device")}
                >
                  Edge Device
                </button>
              </div>
            </div>
          )}  */}

          {!isHostTarget && (
            <>
            <div
              id="llm-family-step"
              ref={familyRef}
              className={`eval-target-hardware-panel__section${
                centerHardwareFamily ? " eval-target-hardware-panel__section--centered" : ""
              }`}
            >
              <label className="eval-target-hardware-panel__label">Hardware family</label>
              <div className="eval-target-hardware-panel__pills">
                {families.map((family) => (
                  <button
                    key={family}
                    type="button"
                    className={`eval-target-hardware-panel__pill${
                      selectedFamily === family ? " is-active" : ""
                    }`}
                    onClick={() => {
                      onFamilyChange?.(family);
                      onChipsetChange?.("");
                      onDeviceChange?.("");
                    }}
                  >
                    {capitalize(family)}
                  </button>
                ))}
              </div>
            </div>

            <div className="eval-target-hardware-panel__split">
              <div
                id="llm-chipset-step"
                ref={chipsetRef}
                className="eval-target-hardware-panel__section"
              >
                <label className="eval-target-hardware-panel__label" htmlFor="eval-target-chipset-select">
                  Chipset
                </label>
                {!selectedFamily ? (
                  <p className="eval-target-hardware-panel__hint">Select family first</p>
                ) : (
                  <Select
                    inputId="eval-target-chipset-select"
                    className="eval-target-hardware-panel__react-select"
                    classNamePrefix="eval-target-device-select"
                    options={chipsetOptions}
                    value={
                      chipsetOptions.find(
                        (option) => option.value === selectedChipset
                      ) || null
                    }
                    placeholder="Select chipset"
                    isDisabled={chipsetOptions.length === 0}
                    isSearchable={false}
                    menuPlacement={resolvedPlacement === "above" ? "top" : "auto"}
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuShouldScrollIntoView={false}
                    styles={selectPortalStyles}
                    onChange={(option) => {
                      onChipsetChange?.(option?.value || "");
                      onDeviceChange?.("");
                    }}
                  />
                )}
              </div>

              <div className="eval-target-hardware-panel__section">
                <label className="eval-target-hardware-panel__label" htmlFor="eval-target-device-select-inline">
                  Test device
                </label>
                {!selectedChipset ? (
                  <p className="eval-target-hardware-panel__hint">Select chipset first</p>
                ) : deviceList.length === 0 ? (
                  <p className="eval-target-hardware-panel__hint">No devices available</p>
                ) : (
                  renderDeviceSelect("eval-target-device-select-inline")
                )}
              </div>
            </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
